import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Shield, ChevronDown, ChevronRight, LayoutDashboard, Wrench } from 'lucide-react';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'program_director', label: 'Program Director' },
  { value: 'manager', label: 'Manager' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'case_manager', label: 'Case Manager' },
  { value: 'residential_aide', label: 'Residential Aide' },
];

// Dashboard Tabs - core operational sections shown in the sidebar
const DASHBOARD_TAB_KEYS = [
  'manager_facilities_tab',
  'manager_clients_tab',
  'manager_rosters_tab',
  'manager_services_tab',
  'manager_notes_tab',
  'manager_staff_tab',
];

// System features - cross-cutting tools, reporting, and admin utilities
const SYSTEM_FEATURE_KEYS: string[] = [
  'manager_inspections_tab',
  'manager_analytics_tab',
  'manager_ai_assistant_tab',
  'manager_shift_report_tab',
  'manager_activity_logs_tab',
  'compliance_module',
];

// Categories for display
const CATEGORY_LABELS = {
  dashboardTabs: 'Manager Dashboard Tabs',
  systemFeatures: 'Modules',
};

export const FeatureToggleManagement = () => {
  const { toggles, isLoading, updateToggle, canManageToggles } = useFeatureToggles();
  const [dashboardTabsOpen, setDashboardTabsOpen] = useState(true);
  const [systemFeaturesOpen, setSystemFeaturesOpen] = useState(true);

  if (!canManageToggles) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-destructive" />
            <CardTitle>Access Denied</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            You don't have permission to manage feature toggles.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  const handleToggleRole = (toggleId: string, currentRoles: string[], role: string) => {
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    
    updateToggle({ id: toggleId, enabled_for_roles: newRoles });
  };

  // Filter toggles by category
  const dashboardTabs = toggles?.filter(t => DASHBOARD_TAB_KEYS.includes(t.feature_key)) || [];
  const systemFeatures = toggles?.filter(t => SYSTEM_FEATURE_KEYS.includes(t.feature_key)) || [];

  const FeatureRow = ({ toggle }: { toggle: typeof toggles extends (infer T)[] | undefined ? T : never }) => {
    if (!toggle) return null;
    
    return (
      <div className="border-b border-border last:border-b-0 py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium text-sm">{toggle.feature_name}</span>
          <Badge variant={toggle.is_active ? 'default' : 'secondary'} className="text-xs">
            {toggle.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        {toggle.description && (
          <p className="text-xs text-muted-foreground mb-3">{toggle.description}</p>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {ROLE_OPTIONS.map((role) => (
            <div key={role.value} className="flex items-center space-x-1.5">
              <Checkbox
                id={`${toggle.id}-${role.value}`}
                checked={toggle.enabled_for_roles.includes(role.value)}
                onCheckedChange={() => 
                  handleToggleRole(toggle.id, toggle.enabled_for_roles, role.value)
                }
                className="h-3.5 w-3.5"
              />
              <Label
                htmlFor={`${toggle.id}-${role.value}`}
                className="text-xs font-normal cursor-pointer text-muted-foreground hover:text-foreground"
              >
                {role.label}
              </Label>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Dashboard Tabs Section */}
      <Card>
        <Collapsible open={dashboardTabsOpen} onOpenChange={setDashboardTabsOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <LayoutDashboard className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base">Dashboard Tabs</CardTitle>
                  <Badge variant="outline" className="text-xs">{dashboardTabs.length}</Badge>
                </div>
                {dashboardTabsOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Core operational sections (Facilities, Clients, Rosters, Services, Notes, Staff)
              </p>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {dashboardTabs.length > 0 ? (
                dashboardTabs.map((toggle) => (
                  <FeatureRow key={toggle.id} toggle={toggle} />
                ))
              ) : (
                <p className="text-sm text-muted-foreground py-4">No dashboard tab toggles configured.</p>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* System Features Section */}
      <Card>
        <Collapsible open={systemFeaturesOpen} onOpenChange={setSystemFeaturesOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base">Modules</CardTitle>
                  <Badge variant="outline" className="text-xs">{systemFeatures.length}</Badge>
                </div>
                {systemFeaturesOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Cross-cutting tools: Inspections, Analytics, AI Assistant, Shift Report, Activity Logs
              </p>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {systemFeatures.length > 0 ? (
                systemFeatures.map((toggle) => (
                  <FeatureRow key={toggle.id} toggle={toggle} />
                ))
              ) : (
                <p className="text-sm text-muted-foreground py-4">No system feature toggles configured.</p>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  );
};
